import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { Popover, Tooltip } from '@vd/ui';
import type { LiveEvent } from '@vd/types';
import { api, type NotificationItem } from '../../lib/api';
import { When } from '../../lib/When';
import { useAuth } from '../auth/AuthProvider';
import { useGuardedNavigate } from '../../app/NavigationGuard';
import styles from './TopBar.module.css';

/** The live events that can bring a notification. */
const NOTIFYING_EVENTS: ReadonlySet<LiveEvent['type']> = new Set<LiveEvent['type']>([
  'ticket.created', 'ticket.assigned', 'comment.created', 'ticket.updated', 'ticket.archived', 'ticket.deleted',
]);

const WHAT: Record<NotificationItem['kind'], string> = {
  assigned: 'assigned you',
  mentioned: 'mentioned you',
  commented: 'commented',
};

/**
 * The bell (TOADO-839): what others did that concerns you in this workspace,
 * newest first, with a badge for what came since it was last opened. Opening
 * it marks them read on the server, but the list keeps showing which were new
 * until it closes.
 */
export function NotificationsBell() {
  const { currentCompanyId } = useAuth();
  const qc = useQueryClient();
  const navigate = useGuardedNavigate();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const key = ['notifications', currentCompanyId];
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => api.listNotifications(currentCompanyId!),
    enabled: !!currentCompanyId,
    refetchOnWindowFocus: true,
  });
  // Kept current without a reload, once a burst of events has settled, and
  // not while open: the list stays as it was opened.
  useEffect(() => {
    if (!currentCompanyId || open) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = api.subscribe((event) => {
      if (!NOTIFYING_EVENTS.has(event.type)) return;
      clearTimeout(timer);
      timer = setTimeout(() => { void qc.invalidateQueries({ queryKey: ['notifications', currentCompanyId] }); }, 500);
    });
    return () => { clearTimeout(timer); unsubscribe(); };
  }, [currentCompanyId, open, qc]);

  // Which opening of the bell is current: each opening, closing, workspace
  // change and unmount takes a new number, so a list that comes back for an
  // earlier one marks nothing (Codex review rounds 3 and 4).
  const opening = useRef(0);
  useEffect(() => () => { opening.current += 1; }, []);
  useEffect(() => {
    opening.current += 1;
    setOpen(false);
  }, [currentCompanyId]);

  const unread = data?.unread ?? 0;
  const close = () => {
    opening.current += 1;
    setOpen(false);
    void qc.invalidateQueries({ queryKey: key });
  };
  const toggle = () => {
    if (open) return close();
    setOpen(true);
    if (!currentCompanyId) return;
    opening.current += 1;
    const thisOpening = opening.current;
    // Read again, then marked seen up to that read's time: something that
    // arrived since the list on screen was fetched is shown, and anything
    // after this read stays new (Codex review).
    // `staleTime: 0`: a cached answer, however recent, is not what opening shows (Codex review round 2).
    void qc.fetchQuery({ queryKey: key, queryFn: () => api.listNotifications(currentCompanyId), staleTime: 0 })
      // Marked only if the bell is still open on that workspace: one closed
      // before its list came back showed nothing (Codex review round 3).
      .then((fresh) => (fresh.unread > 0 && opening.current === thisOpening
        ? api.markInboxSeen(currentCompanyId, 'notifications', fresh.readAt) : undefined))
      .catch(() => { /* still new next time; nothing else depends on it */ });
  };
  const label = unread > 0 ? `Notifications, ${unread} new` : 'Notifications';

  return (
    <>
      <Tooltip content="Notifications" placement="bottom">
        <button ref={button} className={styles.iconBtn} aria-label={label} aria-haspopup="dialog" aria-expanded={open} onClick={toggle}>
          <Bell size={16} />
          {unread > 0 && <span className={styles.badge} data-testid="notifications-badge">{unread > 99 ? '99+' : unread}</span>}
        </button>
      </Tooltip>
      <Popover open={open} onClose={close} anchor={button.current?.getBoundingClientRect() ?? null}>
        <div className={styles.notifications} role="dialog" aria-label="Notifications">
          <div className={styles.notificationsHead}>Notifications</div>
          {(data?.items.length ?? 0) === 0 ? (
            <p className={styles.notificationsEmpty}>
              Nothing yet. This is where you hear when someone assigns you, mentions you, or comments on your tickets.
            </p>
          ) : (
            <ul className={styles.notificationsList}>
              {data!.items.map((item, index) => (
                <li key={`${item.kind}-${item.ticketId}-${item.at}-${index}`}>
                  <button
                    type="button"
                    className={styles.notification}
                    onClick={() => { close(); navigate(`/tickets/${item.ticketId}`); }}
                  >
                    {item.unread && <span className={styles.unreadDot} aria-label="New" />}
                    <span className={styles.notificationText}>
                      <span><strong>{item.actor}</strong> {WHAT[item.kind]}</span>
                      <span className={styles.notificationTitle}>{item.title || 'Untitled ticket'}</span>
                    </span>
                    <When className={styles.notificationTime} at={item.at} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Popover>
    </>
  );
}
